import { X } from "lucide-react";
import { cn } from "@asm/ui";
import { NODE_COLORS, STATUS_COLORS } from "./types";

interface GraphFiltersProps {
  filters: {
    assetTypes: string[];
    status: string | null;
    includePorts: boolean;
  };
  onFiltersChange: (filters: {
    assetTypes: string[];
    status: string | null;
    includePorts: boolean;
  }) => void;
  onClose: () => void;
}

const ASSET_TYPES = [
  { value: "subdomain", label: "Subdomains" },
  { value: "ip", label: "IP Addresses" },
  { value: "port", label: "Ports" },
];

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "stale", label: "Stale" },
  { value: "removed", label: "Removed" },
];

export function GraphFilters({ filters, onFiltersChange, onClose }: GraphFiltersProps) {
  const toggleAssetType = (type: string) => {
    const newTypes = filters.assetTypes.includes(type)
      ? filters.assetTypes.filter((t) => t !== type)
      : [...filters.assetTypes, type];
    onFiltersChange({ ...filters, assetTypes: newTypes });
  };

  const setStatus = (status: string | null) => {
    onFiltersChange({ ...filters, status });
  };

  return (
    <div className="absolute left-4 top-48 w-56 bg-card border border-border rounded-lg shadow-lg z-10">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <span className="text-sm font-medium">Filters</span>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded-md transition-colors"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      </div>

      <div className="p-3 space-y-4">
        {/* Asset Types */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Asset Types
          </label>
          <div className="space-y-1">
            {ASSET_TYPES.map((type) => (
              <label
                key={type.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={filters.assetTypes.length === 0 || filters.assetTypes.includes(type.value)}
                  onChange={() => toggleAssetType(type.value)}
                  className="rounded border-border"
                />
                <span className="flex items-center gap-2 text-sm">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: NODE_COLORS[type.value] }}
                  />
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Status
          </label>
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setStatus(null)}
              className={cn(
                "px-2 py-1 text-xs rounded-md border transition-colors",
                filters.status === null
                  ? "bg-primary/10 border-primary text-primary"
                  : "border-border hover:bg-muted"
              )}
            >
              All
            </button>
            {STATUSES.map((status) => (
              <button
                key={status.value}
                onClick={() => setStatus(status.value)}
                className={cn(
                  "px-2 py-1 text-xs rounded-md border transition-colors",
                  filters.status === status.value
                    ? "border-primary"
                    : "border-border hover:bg-muted"
                )}
                style={{
                  backgroundColor:
                    filters.status === status.value
                      ? `${STATUS_COLORS[status.value]}20`
                      : undefined,
                  color:
                    filters.status === status.value
                      ? STATUS_COLORS[status.value]
                      : undefined,
                }}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>

        {/* Include Ports Toggle */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.includePorts}
              onChange={(e) =>
                onFiltersChange({ ...filters, includePorts: e.target.checked })
              }
              className="rounded border-border"
            />
            <span className="text-sm">Include Ports</span>
          </label>
          <p className="text-xs text-muted-foreground mt-1">
            Ports can make large graphs harder to read
          </p>
        </div>
      </div>
    </div>
  );
}
